import { useEffect, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import api from '../services/api';
import KpiCard from '../components/KpiCard';
import Modal from '../components/Modal';

const COLORS  = ['#e94560', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'];
const fmt     = (n) => Number(n || 0).toLocaleString('fr-FR');
const fmtDate = (d) => d
  ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' })
  : '—';

// ── Tooltip recharts ─────────────────────────────────────────────────────────
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', boxShadow: 'var(--shadow-md)', minWidth: 140 }}>
      <p style={{ fontWeight: 600, marginBottom: 6, fontSize: 13 }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color, fontWeight: 700, fontSize: 13 }}>
          {p.name}: {fmt(p.value)} {p.name?.toLowerCase().includes('rev') ? 'F' : ''}
        </p>
      ))}
    </div>
  );
};

// ── Badges ───────────────────────────────────────────────────────────────────
const KycBadge = ({ s }) => {
  const cfg = {
    verified: ['#dcfce7','#166534','✅ Vérifié'],
    pending:  ['#fef9c3','#854d0e','⏳ En attente'],
    rejected: ['#fee2e2','#991b1b','❌ Rejeté'],
  };
  const [bg, color, label] = cfg[s] || ['#f1f5f9','#64748b', s || 'N/A'];
  return <span style={{ background: bg, color, padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{label}</span>;
};

const NegBadge = ({ s }) => {
  const cfg = {
    paid:             ['#dbeafe','#1e40af','💰 Payé'],
    pending:          ['#fef9c3','#854d0e','⏳ En attente'],
    countered:        ['#fff7ed','#c2410c','↩ Contre-offre'],
    client_countered: ['#f5f3ff','#6d28d9','↩ C. client'],
    cancelled:        ['#f1f5f9','#64748b','✕ Annulé'],
  };
  const [bg, color, label] = cfg[s] || ['#f1f5f9','#64748b', s || '—'];
  return <span style={{ background: bg, color, padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{label}</span>;
};

const SubBadge = ({ s }) => {
  const cfg = {
    active:  ['#dcfce7','#166534','● Actif'],
    expired: ['#fee2e2','#991b1b','● Expiré'],
    pending: ['#fef9c3','#854d0e','● En attente'],
  };
  const [bg, color, label] = cfg[s] || ['#f1f5f9','#64748b', s || '—'];
  return <span style={{ background: bg, color, padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{label}</span>;
};

// ── Contenu modal ─────────────────────────────────────────────────────────────
function ModalContent({ type, data, loading, stats }) {
  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 180 }}>
      <div className="spinner" />
    </div>
  );

  /* Revenus : données statiques déjà chargées dans stats */
  if (type === 'revenue') {
    const items = [
      { label: 'Abonnements prestataires',       value: stats.subscription_revenue, color: '#3b82f6', icon: '🔑' },
      { label: 'Commissions de mise en relation', value: stats.commission_revenue,   color: '#e94560', icon: '💳' },
    ];
    return (
      <div>
        <div style={{ textAlign: 'center', padding: '16px 0 24px', borderBottom: '1px solid var(--border)', marginBottom: 20 }}>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Total toutes sources</p>
          <p style={{ fontSize: 40, fontWeight: 800, color: 'var(--primary)', margin: 0 }}>{fmt(stats.total_revenue)} F</p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {items.map(item => {
            const pct = stats.total_revenue ? (item.value / stats.total_revenue * 100) : 0;
            return (
              <div key={item.label} style={{ background: 'var(--surface-2)', borderRadius: 12, padding: '16px 20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>{item.icon} {item.label}</span>
                  <span style={{ fontSize: 22, fontWeight: 800, color: item.color }}>{fmt(item.value)} F</span>
                </div>
                <div style={{ height: 10, background: 'var(--border)', borderRadius: 5 }}>
                  <div style={{ height: 10, background: item.color, borderRadius: 5, width: `${pct.toFixed(1)}%`, transition: 'width 0.8s ease' }} />
                </div>
                <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>{pct.toFixed(1)}% du total</p>
              </div>
            );
          })}
        </div>
        <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {[
            { label: 'Abonnements actifs',    value: fmt(stats.active_subscriptions) },
            { label: 'Transactions réussies', value: fmt(stats.total_transactions) },
          ].map(r => (
            <div key={r.label} style={{ padding: '12px 16px', background: 'var(--surface-2)', borderRadius: 10, display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
              <span style={{ color: 'var(--text-secondary)' }}>{r.label}</span>
              <strong>{r.value}</strong>
            </div>
          ))}
        </div>
      </div>
    );
  }

  /* Empty state */
  if (!data || data.length === 0) return (
    <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-muted)' }}>
      <p style={{ fontSize: 40, marginBottom: 10 }}>📭</p>
      <p style={{ fontSize: 14 }}>Aucune donnée disponible</p>
    </div>
  );

  const TH = ({ children }) => (
    <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '2px solid var(--border)', whiteSpace: 'nowrap', background: 'var(--surface-2)', position: 'sticky', top: 0 }}>
      {children}
    </th>
  );
  const TD = ({ children, mono, bold, muted, right }) => (
    <td style={{ padding: '10px 14px', fontSize: 13, fontFamily: mono ? 'monospace' : undefined, fontWeight: bold ? 700 : 400, color: muted ? 'var(--text-muted)' : 'var(--text-primary)', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap', textAlign: right ? 'right' : 'left' }}>
      {children}
    </td>
  );
  const tbl  = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
  const wrap = { maxHeight: 460, overflowY: 'auto', borderRadius: 8, border: '1px solid var(--border)' };
  const hov  = { onMouseEnter: e => e.currentTarget.style.background = 'var(--surface-2)', onMouseLeave: e => e.currentTarget.style.background = '' };

  /* Utilisateurs */
  if (['users','providers','clients','kyc_pending'].includes(type)) {
    return (
      <div style={wrap}>
        <table style={tbl}>
          <thead><tr><TH>Nom</TH><TH>Téléphone</TH><TH>Rôle</TH><TH>KYC</TH><TH>Inscription</TH></tr></thead>
          <tbody>
            {data.map(u => (
              <tr key={u.id} {...hov}>
                <TD bold>{u.name}</TD>
                <TD mono>{u.phone}</TD>
                <TD>
                  <span style={{ background: u.role === 'provider' ? '#fef3c7' : '#dbeafe', color: u.role === 'provider' ? '#92400e' : '#1e40af', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>
                    {u.role === 'provider' ? '🔧 Prestataire' : '🛒 Client'}
                  </span>
                </TD>
                <TD><KycBadge s={u.kyc_status} /></TD>
                <TD muted>{fmtDate(u.created_at)}</TD>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  /* Abonnements */
  if (['subscriptions','expired_subs'].includes(type)) {
    return (
      <div style={wrap}>
        <table style={tbl}>
          <thead><tr><TH>Prestataire</TH><TH>Téléphone</TH><TH>Montant</TH><TH>Début</TH><TH>Fin</TH><TH>Statut</TH></tr></thead>
          <tbody>
            {data.map(s => (
              <tr key={s.id} {...hov}>
                <TD bold>{s.provider_name}</TD>
                <TD mono>{s.provider_phone}</TD>
                <TD right><span style={{ color: 'var(--success)', fontWeight: 700 }}>{fmt(s.amount)} F</span></TD>
                <TD muted>{fmtDate(s.start_date)}</TD>
                <TD muted>{fmtDate(s.end_date)}</TD>
                <TD><SubBadge s={s.status} /></TD>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  /* Transactions */
  if (type === 'transactions') {
    return (
      <div style={wrap}>
        <table style={tbl}>
          <thead><tr><TH>Client</TH><TH>Prestataire</TH><TH>Service</TH><TH>Commission</TH><TH>Date</TH></tr></thead>
          <tbody>
            {data.map(t => (
              <tr key={t.id} {...hov}>
                <TD bold>{t.client_name}</TD>
                <TD>{t.provider_name}</TD>
                <TD muted>{(t.service_title || '').slice(0, 24)}{(t.service_title?.length || 0) > 24 ? '…' : ''}</TD>
                <TD right><span style={{ color: 'var(--primary)', fontWeight: 700 }}>{fmt(t.amount)} F</span></TD>
                <TD muted>{fmtDate(t.created_at)}</TD>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  /* Négociations */
  if (['negotiations','all_negotiations','deals'].includes(type)) {
    return (
      <div style={wrap}>
        <table style={tbl}>
          <thead><tr><TH>Service</TH><TH>Client</TH><TH>Prestataire</TH><TH>Statut</TH><TH>Prix final</TH><TH>Date</TH></tr></thead>
          <tbody>
            {data.map(n => (
              <tr key={n.id} {...hov}>
                <TD muted>{(n.service_title || '').slice(0, 18)}{(n.service_title?.length || 0) > 18 ? '…' : ''}</TD>
                <TD bold>{n.client_name}</TD>
                <TD>{n.provider_name}</TD>
                <TD><NegBadge s={n.status} /></TD>
                <TD right>
                  {(n.final_price || n.last_offer_price)
                    ? <span style={{ fontWeight: 700 }}>{fmt(n.final_price || n.last_offer_price)} F</span>
                    : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                </TD>
                <TD muted>{fmtDate(n.created_at)}</TD>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return null;
}

// ── Composant principal ───────────────────────────────────────────────────────
export default function Dashboard() {
  const [stats, setStats]               = useState(null);
  const [loading, setLoading]           = useState(true);
  const [modal, setModal]               = useState(null);
  const [modalData, setModalData]       = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  useEffect(() => {
    api.get('/admin/stats')
      .then(r => setStats(r.data))
      .finally(() => setLoading(false));
  }, []);

  const openModal = async (type, title, url = null) => {
    setModal({ type, title });
    setModalData(null);
    if (!url) return;
    setModalLoading(true);
    try {
      const { data } = await api.get(url);
      setModalData(Array.isArray(data) ? data : (data.subscriptions ?? data.transactions ?? data.rows ?? []));
    } catch {
      setModalData([]);
    } finally {
      setModalLoading(false);
    }
  };

  const closeModal = () => { setModal(null); setModalData(null); };

  if (loading) return (
    <div>
      <div className="page-header">
        <div className="skeleton" style={{ height: 28, width: 200, marginBottom: 8 }} />
      </div>
      <div className="stats-grid">
        {[1,2,3,4,5,6].map(i => <div key={i} className="skeleton" style={{ height: 110, borderRadius: 12 }} />)}
      </div>
    </div>
  );

  const revenueData = [
    { name: 'Abonnements', value: stats.subscription_revenue, color: '#3b82f6' },
    { name: 'Commissions',  value: stats.commission_revenue,   color: '#e94560' },
  ];

  const onEnter = (e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.15)'; };
  const onLeave = (e) => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; };

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2 className="page-title">Vue d'ensemble</h2>
          <p className="page-subtitle">Tableau de bord Elite Services</p>
        </div>
        <span style={{ fontSize: 12, color: 'var(--text-muted)', background: 'var(--surface-2)', padding: '6px 14px', borderRadius: 'var(--radius-full)' }}>
          🕐 Mis à jour maintenant
        </span>
      </div>

      {/* ── KPI Row 1 — Revenus ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 16 }}>
        <div
          style={{ gridColumn: 'span 1', background: 'linear-gradient(135deg, #e94560 0%, #c73652 100%)', borderRadius: 'var(--radius-xl)', padding: '24px 28px', color: '#fff', position: 'relative', overflow: 'hidden', boxShadow: '0 8px 32px rgba(233,69,96,0.3)', cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s', userSelect: 'none' }}
          onClick={() => openModal('revenue', '💰 Détail des revenus')}
          onMouseEnter={onEnter}
          onMouseLeave={onLeave}
        >
          <div style={{ position: 'absolute', top: -20, right: -20, width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
          <p style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, opacity: 0.8, marginBottom: 8 }}>Revenus totaux</p>
          <p style={{ fontSize: 32, fontWeight: 800, margin: 0 }}>{fmt(stats.total_revenue)} F</p>
          <div style={{ display: 'flex', gap: 16, marginTop: 12 }}>
            <span style={{ fontSize: 11, opacity: 0.9 }}>🔑 Abonnements : {fmt(stats.subscription_revenue)} F</span>
            <span style={{ fontSize: 11, opacity: 0.9 }}>💳 Commissions : {fmt(stats.commission_revenue)} F</span>
          </div>
          <p style={{ fontSize: 11, opacity: 0.65, marginTop: 10 }}>Cliquer pour le détail →</p>
        </div>

        <KpiCard
          label="Abonnements actifs"
          value={stats.active_subscriptions}
          icon="🔑" color="var(--info)"
          sublabel={`${stats.users?.providers || 0} prestataires`}
          onClick={() => openModal('subscriptions', '🔑 Abonnements actifs', '/admin/subscriptions?status=active&limit=100')}
        />
        <KpiCard
          label="Mises en relation"
          value={stats.total_transactions}
          icon="🤝" color="var(--success)"
          sublabel={`${stats.negotiations?.deals_closed || 0} deals conclus`}
          onClick={() => openModal('transactions', '🤝 Commissions perçues', '/admin/payments?status=success&limit=100')}
        />
      </div>

      {/* ── KPI Row 2 — Utilisateurs & Activité ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 20 }}>
        <KpiCard label="Utilisateurs actifs"    value={stats.users?.total || 0}          icon="👥" color="#8b5cf6" sublabel="Total inscrits"     onClick={() => openModal('users',        '👥 Tous les utilisateurs',          '/admin/users?limit=100')} />
        <KpiCard label="Prestataires"            value={stats.users?.providers || 0}       icon="🔧" color="#f59e0b" sublabel="Rôle prestataire"   onClick={() => openModal('providers',    '🔧 Prestataires',                   '/admin/users?role=provider&limit=100')} />
        <KpiCard label="Clients"                 value={stats.users?.clients || 0}         icon="🛒" color="#3b82f6" sublabel="Rôle client"        onClick={() => openModal('clients',      '🛒 Clients',                        '/admin/users?role=client&limit=100')} />
        <KpiCard label="Négociations en cours"   value={stats.negotiations?.ongoing || 0}  icon="💬" color="#10b981" sublabel="Actives"            onClick={() => openModal('negotiations', '💬 Négociations en cours',          '/admin/negotiations?status=ongoing&limit=100')} />
      </div>

      {/* ── Graphiques ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        <div className="card animate-fade">
          <div className="card-header"><span className="card-title">💰 Répartition des revenus</span></div>
          <div className="card-body" style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
            <ResponsiveContainer width="60%" height={200}>
              <PieChart>
                <Pie data={revenueData} dataKey="value" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={4}>
                  {revenueData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip formatter={v => [`${fmt(v)} F`]} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ flex: 1 }}>
              {revenueData.map(d => (
                <div key={d.name} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ width: 10, height: 10, borderRadius: '50%', background: d.color, display: 'inline-block' }} />
                      {d.name}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 700 }}>{fmt(d.value)} F</span>
                  </div>
                  <div style={{ height: 4, background: 'var(--border)', borderRadius: 2 }}>
                    <div style={{ height: 4, background: d.color, borderRadius: 2, width: `${stats.total_revenue ? (d.value / stats.total_revenue * 100) : 0}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="card animate-fade" style={{ animationDelay: '0.1s' }}>
          <div className="card-header"><span className="card-title">📊 Catégories les plus demandées</span></div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={stats.top_categories} barSize={28}>
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: 'var(--surface-2)' }} />
                <Bar dataKey="demand" name="Demandes" fill="var(--primary)" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* ── Résumé d'activité ── */}
      <div className="card animate-fade" style={{ animationDelay: '0.2s' }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span className="card-title">📋 Résumé d'activité</span>
          <a href="/reports" style={{ fontSize: 12, color: 'var(--primary)', fontWeight: 600, textDecoration: 'none' }}>Voir les rapports →</a>
        </div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
            {[
              { label: 'Total négociations',   value: stats.negotiations?.total || 0,   icon: '💬', bg: '#eff6ff', color: '#3b82f6', type: 'all_negotiations', title: '💬 Toutes les négociations',          url: '/admin/negotiations?limit=100' },
              { label: 'Deals conclus',        value: stats.negotiations?.deals_closed || 0, icon: '🤝', bg: '#ecfdf5', color: '#10b981', type: 'deals',           title: '🤝 Deals conclus',                   url: '/admin/negotiations?status=paid&limit=100' },
              { label: 'En attente KYC',       value: stats.kyc_pending ?? '—',         icon: '⏳', bg: '#fef3c7', color: '#d97706', type: 'kyc_pending',     title: '⏳ Utilisateurs en attente de KYC',  url: '/admin/users?kyc_status=pending&limit=100' },
              { label: 'Abonnements expirés',  value: stats.expired_subscriptions ?? '—', icon: '⏰', bg: '#fee2e2', color: '#ef4444', type: 'expired_subs',    title: '⏰ Abonnements expirés',              url: '/admin/subscriptions?status=expired&limit=100' },
            ].map(item => (
              <div
                key={item.label}
                style={{ background: item.bg, borderRadius: 12, padding: '16px 20px', cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s', userSelect: 'none' }}
                onClick={() => openModal(item.type, item.title, item.url)}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.1)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
              >
                <p style={{ fontSize: 20, marginBottom: 6 }}>{item.icon}</p>
                <p style={{ fontSize: 26, fontWeight: 800, color: item.color, margin: 0 }}>{item.value}</p>
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>{item.label}</p>
                <p style={{ fontSize: 11, color: item.color, marginTop: 6, opacity: 0.75 }}>Voir le détail →</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Modal détail ── */}
      <Modal open={!!modal} onClose={closeModal} title={modal?.title || ''} maxWidth={820}>
        <ModalContent
          type={modal?.type}
          data={modalData}
          loading={modalLoading}
          stats={stats}
        />
      </Modal>
    </div>
  );
}
